import { FitbitStoredRecord } from '../../../services/fitbitCollectionDataService';
import { humanize } from '../collection/metrics/metricPresentation';
import { measurementObject } from './structuredMeasurements';

const dayMs = 86400000;
const conflict = '\u0000conflict';
const activityLevels = ['SEDENTARY', 'LIGHTLY_ACTIVE', 'FAIRLY_ACTIVE', 'VERY_ACTIVE'];
const zoneLevels = ['LIGHT', 'MODERATE', 'VIGOROUS', 'PEAK'];
export const durationTypes = ['activity-level', 'time-in-heart-rate-zone', 'sedentary-period'];
type Point = { date: string; [key: string]: string | number | null };

// Integrate the union of recorded intervals, splitting at UTC midnight. Concurrent
// different labels are kept as conflicts rather than counted in both categories.
export function intervalMeasurements(records: FitbitStoredRecord[], dates: string[], dataType = 'activity-level') {
  const knownLevels = dataType === 'time-in-heart-rate-zone' ? zoneLevels : activityLevels;
  const colors = dataType === 'time-in-heart-rate-zone' ? ['#438ca2', '#c39224', '#da7043', '#ad3455'] : ['#788799', '#438ca2', '#c39224', '#ad3455'];
  const events: Array<{ at: number; level: string; delta: number }> = [];
  const lower = Date.parse(`${dates[0]}T00:00:00Z`);
  const upper = Date.parse(`${dates[dates.length - 1]}T00:00:00Z`) + dayMs;
  let invalidIntervals = 0;
  for (const record of records) {
    if (record.dataType !== dataType || record.valueState !== 'present') continue;
    const start = record.observedAt ? Date.parse(record.observedAt) : NaN;
    const end = record.observedEndAt ? Date.parse(record.observedEndAt) : NaN;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) { invalidIntervals++; continue; }
    const from = Math.max(start, lower);
    const to = Math.min(end, upper);
    if (!(to > from)) continue;
    const measurement = measurementObject(record.measurement);
    const category = dataType === 'sedentary-period' ? 'SEDENTARY' :
      dataType === 'time-in-heart-rate-zone' ? measurement.heartRateZoneType : measurement.activityLevelType;
    const level = typeof category === 'string' && category.trim() ? category.trim() : 'UNSPECIFIED';
    events.push({ at: from, level, delta: 1 }, { at: to, level, delta: -1 });
  }
  events.sort((a, b) => a.at - b.at);
  const active = new Map<string, number>();
  const days = new Map<string, Map<string, number>>();
  const groups = new Set<string>();
  let cursor = events[0]?.at;
  let conflictingMinutes = 0;
  for (const event of events) {
    if (event.at > cursor && active.size) {
      const level = active.size > 1 ? conflict : active.keys().next().value!;
      let from = cursor;
      while (from < event.at) {
        const to = Math.min(event.at, (Math.floor(from / dayMs) + 1) * dayMs);
        const date = new Date(from).toISOString().slice(0, 10);
        const minutes = (to - from) / 60000;
        const totals = days.get(date) || new Map<string, number>();
        totals.set(level, (totals.get(level) || 0) + minutes);
        days.set(date, totals); groups.add(level);
        if (level === conflict) conflictingMinutes += minutes;
        from = to;
      }
    }
    const count = (active.get(event.level) || 0) + event.delta;
    if (count > 0) active.set(event.level, count); else active.delete(event.level);
    cursor = event.at;
  }
  const rank = (level: string) => level === conflict ? 6 : knownLevels.includes(level) ? knownLevels.indexOf(level) : 4;
  const series = Array.from(groups).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).map((level, index) => ({
    key: `group${index}`,
    label: level === conflict ? 'Conflicting labels' : level.endsWith('UNSPECIFIED') ? 'Unspecified' : humanize(level),
    color: level === conflict ? '#754a98' : colors[knownLevels.indexOf(level)] || '#3f8770',
    level,
  }));
  const points: Point[] = dates.map(date => ({ date,
    ...Object.fromEntries(series.map(group => [group.key, days.get(date)?.get(group.level) ?? null])),
  }));
  return { series, points, invalidIntervals, conflictingMinutes };
}
